/**
 * Registered push devices — list + revoke for the Notifications settings.
 *
 * Devices live at `tenants/{tenantId}/users/{uid}/devices/{token}` (written by
 * `push.ts` on enable). The dispatcher fans a push out to every token here, so
 * the user needs to see and prune them. Reads/writes go straight to Firestore
 * via the shared `praesto` app, tenant- and user-scoped (same pattern as
 * `push.ts`); the rules allow the owning user to read/delete their own tokens.
 */
import { getApp } from "firebase/app";
import {
  collection,
  deleteDoc,
  doc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { deleteToken, getMessaging } from "firebase/messaging";

export interface RegisteredDevice {
  readonly token: string;
  readonly userAgent?: string;
  readonly createdAt?: { seconds: number; nanoseconds: number };
  readonly lastSeenAt?: { seconds: number; nanoseconds: number };
}

export type Unsubscribe = () => void;

function db() {
  return getFirestore(getApp("praesto"));
}
const devicesPath = (tenantId: string, uid: string) => `tenants/${tenantId}/users/${uid}/devices`;

/** Subscribe to the user's registered devices, newest first. */
export function subscribeDevices(
  tenantId: string,
  uid: string,
  cb: (devices: ReadonlyArray<RegisteredDevice>) => void,
): Unsubscribe {
  const q = query(collection(db(), devicesPath(tenantId, uid)), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ ...(d.data() as object), token: d.id }) as RegisteredDevice));
  });
}

/**
 * Revoke a device: delete its `devices/{token}` doc. If it is the token of the
 * device the user is currently on, also delete the live FCM token so push stops
 * immediately (and the toggle reflects "off").
 */
export async function removeDevice(
  tenantId: string,
  uid: string,
  token: string,
  isCurrentDevice: boolean,
): Promise<void> {
  await deleteDoc(doc(db(), `${devicesPath(tenantId, uid)}/${token}`));
  if (isCurrentDevice) {
    try {
      await deleteToken(getMessaging(getApp("praesto")));
    } catch {
      // Best effort: the Firestore doc is gone, which is what stops delivery.
    }
  }
}

/**
 * Friendly label from a stored userAgent, e.g. "Chrome · Android".
 *
 * Brand tokens ("Chrome", "Android", …) are not translatable. The only
 * user-facing copy is the generic fallback when the OS can't be derived; this
 * is a non-React lib without `t`, so the caller injects the localized fallback
 * (e.g. `t("settings.notifications.device.fallback")`) to keep this function
 * pure and Italian out of .ts (project rule #7).
 */
export function deviceLabel(ua: string | undefined, fallback: string): string {
  if (!ua) return fallback;
  const os = /Android/.test(ua)
    ? "Android"
    : /iPhone/.test(ua)
      ? "iPhone"
      : /iPad/.test(ua)
        ? "iPad"
        : /Macintosh|Mac OS/.test(ua)
          ? "Mac"
          : /Windows/.test(ua)
            ? "Windows"
            : /Linux/.test(ua)
              ? "Linux"
              : fallback;
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
      ? "Chrome"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Safari\//.test(ua)
          ? "Safari"
          : "";
  return browser ? `${browser} · ${os}` : os;
}
